import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { Form } from "antd";
import CommonDatePicker from "@/common/elements/DatePicker";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import TimePicker from "@/common/elements/TimePicker";
import { StyledGridCol, StyledGridRow, StyledSectionHeading } from "@/common/styles";
import dayjs from "dayjs";
import { getOptionsFromRefList } from "@/util/common_function";

const toDayjs = (val) => {
  if (!val) return null;
  if (dayjs.isDayjs(val)) return val;
  const parsed = dayjs(val, "MM/DD/YYYY", true);
  return parsed.isValid() ? parsed : dayjs(val);
};

const toDayjsTime = (val) => {
  if (!val) return null;
  if (dayjs.isDayjs(val)) return val;
  if (Array.isArray(val)) return dayjs(`${String(val[0]).padStart(2,"0")}:${String(val[1]).padStart(2,"0")}`, "HH:mm");
  const parsed = dayjs(val, "HH:mm", true);
  return parsed.isValid() ? parsed : null;
};

const ServiceAttemptDetailForm = forwardRef(({
  serviceType = null,
  serviceDoc  = null,
  isReadOnly  = false,
  prefilledAddress = null,
  onSave,
}, ref) => {

    const [antForm] = Form.useForm();
    const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
    const staffId     = userDetails?.staffId || userDetails?.staffID || null;

    const isAddMode = !serviceType;

    const [form, setForm] = useState({
        serviceTypeID:       serviceType?.serviceTypeID       ?? 0,
        serviceDocumentID:   serviceType?.serviceDocumentID   ?? 0,
        successful:          serviceType?.serviceStatus === "served",
        dateServed:          serviceType?.dateServed || dayjs().format("MM/DD/YYYY"),
        timeServed:          serviceType?.timeServed       || dayjs().format("HH:mm"),
        servedByStaffID:     serviceType?.servedByStaffID  ?? staffId,
        deputyTime:          serviceType?.deputyTime          ?? null,
        serviceType:         serviceType?.serviceType         ?? null,
        serviceStatus:       serviceType?.serviceStatus       ?? null,
        comments:            serviceType?.comments            ?? "",
        certificateComments: serviceType?.serviceInstructions ?? "",
        streetNumber: isAddMode ? (prefilledAddress?.addressStreetNumber    ?? "") : "",
        street1:      isAddMode ? (prefilledAddress?.addressStreet1         ?? "") : "",
        street2:      isAddMode ? (prefilledAddress?.addressStreet2         ?? "") : "",
        apt:          isAddMode ? (prefilledAddress?.addressApartmentNumber ?? "") : "",
        city:         isAddMode ? (prefilledAddress?.addressCity            ?? "") : "",
        state:        isAddMode ? (prefilledAddress?.addressState           ?? null) : null,
        zip:          isAddMode ? (prefilledAddress?.addressZip             ?? "") : "",
    });

  // Seed the antd Form's initial field values once on mount, so the
  // free-typed inputs start with the right text without being controlled
  // by `form` state on every keystroke.
  useEffect(() => {
    antForm.setFieldsValue({
      deputyTime:          form.deputyTime,
      comments:            form.comments,
      certificateComments: form.certificateComments,
      streetNumber:        form.streetNumber,
      street1:             form.street1,
      street2:             form.street2,
      apt:                 form.apt,
      city:                form.city,
      zip:                 form.zip,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patch = (fields) => setForm((prev) => ({ ...prev, ...fields }));

  // One shared debounce timer ref per field, keyed by field name
  const debounceTimers = useRef({});

  const patchDebounced = (key) => (value) => {
    if (debounceTimers.current[key]) clearTimeout(debounceTimers.current[key]);
    debounceTimers.current[key] = setTimeout(() => {
      patch({ [key]: value });
    }, 300);
  };

  // Cleanup all debounce timers on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimers.current).forEach((t) => t && clearTimeout(t));
    };
  }, []);

  useImperativeHandle(ref, () => ({
    submit() {
      // Flush any pending debounced fields before reading final values, so a
      // fast type-then-immediately-click-Save doesn't drop the last edit.
      Object.values(debounceTimers.current).forEach((t) => t && clearTimeout(t));
      const latest = antForm.getFieldsValue();
      const finalForm = { ...form, ...latest };
      if (typeof onSave === "function") onSave(finalForm);
    },
  }));

  return (
    <Form form={antForm} disabled={isReadOnly}>
      <StyledSectionHeading>
        <span>Document Service Attempt Detail</span>
      </StyledSectionHeading>

      {/* Successful checkbox */}
      <StyledGridRow gutter={16}>
        <StyledGridCol>
          <FormItem label="Successful" layout="horizontal" labelPostion="label-right">
            <Checkbox
                checked={form.successful}
                onChange={(e) => patch({ 
                    successful: e.target.checked,
                    serviceStatus: e.target.checked ? "served" : null,
                })}
                disabled={isReadOnly}
            />
          </FormItem>
        </StyledGridCol>
      </StyledGridRow>

      {/* Date, Time, Officer, Deputy Time */}
      <StyledGridRow gutter={16}>
        <StyledGridCol>
          <FormItem label="Date of Attempt">
            <CommonDatePicker
              showTime={false}
              format="MM/DD/YYYY"
              value={toDayjs(form.dateServed)}
              onChange={(_, ds) => patch({ dateServed: ds })}
              disabled={isReadOnly}
            />
          </FormItem>
        </StyledGridCol>

        <StyledGridCol>
          <FormItem label="Time of Attempt">
            <TimePicker
              value={toDayjsTime(form.timeServed)}
              onChange={(v) => patch({ timeServed: v ? dayjs(v).format("HH:mm") : null })}
              disabled={isReadOnly}
            />
          </FormItem>
        </StyledGridCol>

        <StyledGridCol lg={6}>
          <FormItem label="Serving Officer">
            <Select
              value={form.servedByStaffID}
              onChange={(v) => patch({ servedByStaffID: v })}
              disabled={isReadOnly}
              options={getOptionsFromRefList("", true, true, true)}
            />
          </FormItem>
        </StyledGridCol>

        <StyledGridCol>
          <FormItem label="Deputy Time (hours)" name="deputyTime">
            <TextField
              onChange={(e) => patchDebounced("deputyTime")(e.target.value)}
              disabled={isReadOnly}
            />
          </FormItem>
        </StyledGridCol>
      </StyledGridRow>

      {/* Type Of Service, Service Status */}
      <StyledGridRow gutter={16}>
        <StyledGridCol>
          <FormItem label="Type Of Service">
            <Select
              value={form.serviceType}
              onChange={(v) => patch({ serviceType: v })}
              disabled={isReadOnly}
              options={getOptionsFromRefList("ServiceType")}
            />
          </FormItem>
        </StyledGridCol>

        <StyledGridCol>
          <FormItem label="Service Status">
            <Select
                value={form.serviceStatus}
                onChange={(v) => patch({ 
                    serviceStatus: v,
                    successful: v === "served",
                })}
                disabled={isReadOnly}
                options={getOptionsFromRefList("ServiceStatus")}
            />
          </FormItem>
        </StyledGridCol>
      </StyledGridRow>

      {/* Comments */}
      <StyledGridRow gutter={16}>
        <StyledGridCol lg={24}>
          <FormItem label="Comments" name="comments">
            <TextField
              inputType="textarea"
              rows="3"
              onChange={(e) => patchDebounced("comments")(e.target.value)}
              disabled={isReadOnly}
            />
          </FormItem>
        </StyledGridCol>
      </StyledGridRow>

      {/* Certificate Comments */}
      <StyledGridRow gutter={16}>
        <StyledGridCol lg={24}>
          <FormItem label="Certificate Comments" name="certificateComments">
            <TextField
              inputType="textarea"
              rows="3"
              onChange={(e) => patchDebounced("certificateComments")(e.target.value)}
              disabled={isReadOnly}
            />
          </FormItem>
        </StyledGridCol>
      </StyledGridRow>

      {/* Address Where Service Was Attempted */}
      <StyledSectionHeading>
        <span>Address Where Service Was Attempted</span>
      </StyledSectionHeading>

      <StyledGridRow gutter={16}>
        <StyledGridCol>
          <FormItem label="#" name="streetNumber">
            <TextField
              onChange={(e) => patchDebounced("streetNumber")(e.target.value)}
              disabled={isReadOnly}
            />
          </FormItem>
        </StyledGridCol>

        <StyledGridCol lg={8}>
          <FormItem label="Street" name="street1">
            <TextField
              onChange={(e) => patchDebounced("street1")(e.target.value)}
              disabled={isReadOnly}
            />
          </FormItem>
        </StyledGridCol>

        <StyledGridCol lg={8}>
          <FormItem label="Street 2" name="street2">
            <TextField
              onChange={(e) => patchDebounced("street2")(e.target.value)}
              disabled={isReadOnly}
            />
          </FormItem>
        </StyledGridCol>

        <StyledGridCol>
          <FormItem label="Apt #" name="apt">
            <TextField
              onChange={(e) => patchDebounced("apt")(e.target.value)}
              disabled={isReadOnly}
            />
          </FormItem>
        </StyledGridCol>

        <StyledGridCol>
          <FormItem label="City" name="city">
            <TextField
              onChange={(e) => patchDebounced("city")(e.target.value)}
              disabled={isReadOnly}
            />
          </FormItem>
        </StyledGridCol>

        <StyledGridCol lg={4}>
          <FormItem label="ST">
            <Select
              value={form.state}
              onChange={(v) => patch({ state: v })}
              disabled={isReadOnly}
              options={getOptionsFromRefList("USState")}
              showSearch={false}
            />
          </FormItem>
        </StyledGridCol>

        <StyledGridCol>
          <FormItem label="Zip Code" name="zip">
            <TextField
              onChange={(e) => patchDebounced("zip")(e.target.value)}
              disabled={isReadOnly}
            />
          </FormItem>
        </StyledGridCol>
      </StyledGridRow>
    </Form>
  );
});

export default ServiceAttemptDetailForm;
